import { useEffect, useState } from "react";
import { MagnifyingGlass, MapPin, SpinnerGap } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export function EasyStayConnect({ selectedId, onSelect }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (!q) { setResults(null); setFailed(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const { data } = await api.get("/easystay/properties", { params: { search: q } });
        setResults(data);
      } catch {
        setResults([]);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  return (
    <div className="space-y-3" data-testid="easystay-connect">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search your listing by name, city or address…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          data-testid="easystay-search-input"
        />
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="easystay-search-loading">
          <SpinnerGap size={14} className="animate-spin" /> Searching easystayzm.com…
        </div>
      )}
      {!loading && results && results.length === 0 && (
        <div className="border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="easystay-search-empty">
          {failed ? "Could not reach EasyStay — try again in a moment." : "No matching listings found on easystayzm.com."}
        </div>
      )}
      {results && results.length > 0 && (
        <div className="max-h-64 space-y-2 overflow-y-auto" data-testid="easystay-search-results">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p)}
              data-testid={`easystay-result-${p.id}`}
              className={`flex w-full items-center gap-3 border p-2.5 text-left transition-colors ${selectedId === p.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
            >
              {p.photo ? (
                <img src={p.photo} alt="" className="h-11 w-14 shrink-0 rounded-sm object-cover" />
              ) : (
                <div className="flex h-11 w-14 shrink-0 items-center justify-center rounded-sm bg-muted">
                  <MapPin size={16} className="text-muted-foreground" />
                </div>
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{p.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{[p.address, p.city].filter(Boolean).join(", ")}</span>
              </span>
              {selectedId === p.id && <span className="ml-auto shrink-0 text-xs font-semibold text-primary">Selected</span>}
            </button>
          ))}
        </div>
      )}
      {!results && !loading && (
        <p className="text-xs text-muted-foreground">
          Type a few letters of your listing's name or address — matches from easystayzm.com appear here.
          The listing ID is stored automatically when you pick one; you never need to copy it yourself.
        </p>
      )}
    </div>
  );
}
